import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, MessageCircleQuestion, ClipboardList, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import Avatar from '@/components/common/Avatar';
import type { BajaPaciente, Paso1State, Paso2State, Paso3State } from '../../../hooks/api/baja';

const DECISION_LABEL: Record<string, { emoji: string; label: string }> = {
  derivar: { emoji: '🔄', label: 'Derivar a otra psicóloga' },
  acompanar: { emoji: '✅', label: 'Acompañar hasta alta natural' },
  alta: { emoji: '🏁', label: 'Otra situación' },
};

interface Props {
  patients: BajaPaciente[];
  paso1: Paso1State | null;
  paso2: Paso2State | null;
  paso3: Paso3State | null;
  onBack: () => void;
  onNext: () => void;
}

export const Paso4Confirmacion: React.FC<Props> = ({ patients, paso1, paso2, paso3, onBack, onNext }) => {
  const [accepted, setAccepted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const declared = Object.entries(paso2?.planes ?? {});

  return (
    <div className="mx-auto w-full max-w-2xl space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Confirmación y envío</h1>
        <p className="text-sm text-muted-foreground">Revisá que todo esté correcto antes de continuar al último paso.</p>
      </header>

      <section className="rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
            <MessageCircleQuestion className="h-4 w-4" />
          </span>
          <h2 className="text-base font-bold text-gray-900">Paso 1 — Motivo</h2>
        </div>
        <p className="text-sm text-gray-700 ml-[42px]">{paso1?.reasonOther || paso1?.reason || '—'}</p>
        {!!paso1?.recommend && (
          <p className="text-xs text-muted-foreground ml-[42px]">Recomendación: {paso1.recommend}/5</p>
        )}
      </section>

      <section className="rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
            <ClipboardList className="h-4 w-4" />
          </span>
          <h2 className="text-base font-bold text-gray-900">Pasos 2 y 3 — Procesos declarados</h2>
        </div>
        {declared.length === 0 ? (
          <p className="text-sm text-muted-foreground ml-[42px]">No declaraste procesos activos.</p>
        ) : (
          <div className="space-y-1">
            {declared.map(([patientId, plan]) => {
              const patient = patients.find((p) => p.id === patientId);
              const hcOk = paso3?.[patientId]?.confirmado;
              const decision = DECISION_LABEL[plan.decision];
              return (
                <div key={patientId} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <Avatar name={patient?.nombre ?? patientId} sizeRem={2} className="shrink-0" />
                  <span className="flex-1 text-sm font-medium text-gray-900">{patient?.nombre ?? patientId}</span>
                  <span className="text-muted-foreground text-xs flex items-center gap-1.5 shrink-0">
                    <span aria-hidden>{decision?.emoji}</span> {decision?.label ?? plan.decision}
                    {hcOk && <span className="text-emerald-600 font-medium">· HC ✓</span>}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-primary/25 bg-primary/5 p-5 sm:p-6 space-y-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary shrink-0">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <h2 className="text-base font-bold text-gray-900">Confirmación final</h2>
        </div>
        <label className="flex items-start gap-2 text-sm text-gray-800 pl-[2px]">
          <input type="checkbox" className="accent-primary mt-0.5" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          Confirmo y acepto lo declarado en los pasos anteriores. Entiendo que esta información será revisada por el
          equipo de Psi Mammoliti como parte del cierre formal de mi vínculo.
        </label>
      </section>

      <div className="flex justify-between pt-4 border-t border-border">
        <Button type="button" variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Volver
        </Button>
        <Button type="button" disabled={!accepted} onClick={() => setConfirmOpen(true)} className="sm:min-w-[200px]">
          Confirmar <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Confirmás el envío?</DialogTitle>
            <DialogDescription>
              Una vez confirmado vas a pasar al último paso para declarar la coordinación de tu reunión de cierre con
              administracion@psimammoliti.com. No vas a poder modificar lo declarado en los pasos anteriores.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
            <Button type="button" onClick={() => { setConfirmOpen(false); onNext(); }}>Confirmar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Paso4Confirmacion;
